import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Users, Search } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';
import { getCurrentLanguage } from '../utils/translations';
import { CardLoader } from '../components/Loader';

const ProjetPage = () => {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => setCurrentLanguage(getCurrentLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get('/api/projects/');
        setProjects(response.data || []);
        setFiltered(response.data || []);
      } catch {
        setProjects([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Filtrage par recherche
  useEffect(() => {
    if (!search.trim()) {
      setFiltered(projects);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(projects.filter(p => {
      const title = (p.title_fr || p.title || '').toLowerCase();
      const desc  = (p.description_fr || p.description || '').toLowerCase();
      return title.includes(q) || desc.includes(q);
    }));
  }, [search, projects]);

  const getField = (p, fr, en) => currentLanguage === 'en' ? (p[en] || p[fr] || '') : (p[fr] || p[en] || '');

  const fr = currentLanguage === 'fr';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Bannière ── */}
      <section className="relative h-[280px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#07a050] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-[0.12]" style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
                              radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2px, transparent 2px)`,
            backgroundSize: '120px 120px, 100px 100px'
          }} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10 max-w-4xl mx-auto px-4">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                <FolderOpen className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-semibold text-white/90 uppercase tracking-wider">
                {fr ? 'Action-Humanitaire Bénin' : 'Action-Humanitaire Benin'}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg">
              {fr ? 'Nos Projets' : 'Our Projects'}
            </h1>
            <p className="text-sm sm:text-base text-white/90 max-w-2xl mx-auto leading-relaxed">
              {fr
                ? "Découvrez l'ensemble de nos projets humanitaires et de développement au Bénin."
                : 'Discover all our humanitarian and development projects in Benin.'}
            </p>
            <div className="w-20 h-1 bg-white/50 mx-auto mt-5 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── Contenu ── */}
      <main className="max-w-7xl mx-auto px-4 py-10">

        {/* Barre de recherche */}
        <div className="flex items-center gap-3 mb-8 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={fr ? 'Rechercher un projet...' : 'Search a project...'}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#089556] focus:border-transparent text-sm outline-none"
            />
          </div>
          {!loading && (
            <span className="text-sm text-gray-400 whitespace-nowrap">
              {filtered.length} {fr ? 'projet(s)' : 'project(s)'}
            </span>
          )}
        </div>

        {/* Grille */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <><CardLoader /><CardLoader /><CardLoader /><CardLoader /><CardLoader /><CardLoader /></>
          ) : filtered.length === 0 ? (
            <div className="col-span-full text-center py-20">
              <FolderOpen className="h-16 w-16 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500">
                {fr ? 'Aucun projet trouvé.' : 'No project found.'}
              </p>
            </div>
          ) : (
            filtered.map(project => {
              const title = getField(project, 'title_fr', 'title_en') || project.title || '';
              const description = getField(project, 'description_fr', 'description_en') || project.description || '';
              const imageUrl = project.image_url || project.image || '';

              return (
                <div key={project.id} className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col">

                  {/* Barre top */}
                  <div className="h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB]" />

                  {/* Image */}
                  <div className="w-full h-44 overflow-hidden bg-gray-50">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FolderOpen className="h-12 w-12 text-gray-200" />
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-base font-bold text-gray-800 group-hover:text-[#089556] transition-colors leading-snug">
                        {title}
                      </h3>
                      {project.stats && (
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#089556]/10 text-[#089556]">
                          <Users className="h-3 w-3" />
                          {project.stats}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 flex-1 mb-4">
                      {description}
                    </p>

                    <Link
                      to={`/${currentLanguage}/projet/${project.slug || project.id}`}
                      className="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white text-sm font-medium rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                    >
                      {fr ? 'En savoir plus' : 'Learn more'}
                      <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProjetPage;
